import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';

import ReceiptIcon from '@material-ui/icons/Receipt';

import List from '~/containers/List';
import Request from '~/providers/Request';
import ModalDelete from '~/components/ModalDelete';
import { showProduct, editProduct, createProduct } from '~/config/routes';
import { AbilityContext } from '~/contexts/Can';
import { packageOptions, packagesColors } from '~/config/constants';

import ProductContext from './context';

function ProductList() {
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState({ show: false });
  const { delRecord } = useContext(ProductContext);
  const ability = useContext(AbilityContext);
  const listEl = useRef(null);

  const trAttr = (attr) => t(`products.attributes.${attr}`);

  function handleDeletionModal(id, title) {
    setModalInfo({ show: true, id, title });
  }

  async function handleDelete() {
    try {
      await Request.del(`/admin/products/${modalInfo.id}`);
      delRecord(modalInfo.id);
      listEl.current.onQueryChange();
      toast.success(t('delete-toast-success'));
    } catch (e) {
      toast.error(t('delete-toast-fail'));
    } finally {
      setModalInfo({ show: false });
    }
  }

  const actions = ['search', 'filter'];

  if (ability.can('new', 'Products')) {
    actions.push('new');
  }
  if (ability.can('edit', 'Products')) {
    actions.push('edit');
  }
  if (ability.can('delete', 'Products')) {
    actions.push('del');
  }
  if (ability.can('show', 'Products')) {
    actions.push('show');
  }

  return (
    <div>
      <List
        deleteAction={handleDeletionModal}
        routeToNew={createProduct}
        routeToEdit={editProduct}
        routeToShow={showProduct}
        listEl={listEl}
        path={'/admin/products'}
        actions={actions}
        paperProps={{
          Icon: ReceiptIcon,
          iconColor: 'blue',
          title: t('products.defPlural'),
        }}
        columns={[
          {
            title: trAttr('id'),
            field: 'id',
            type: 'numeric',
            precision: 0,
            includeThousandsSeparator: false,
          },
          { title: trAttr('ean'), field: 'ean', type: 'string' },
          {
            title: trAttr('name'),
            field: 'name',
            type: 'string',
          },
          {
            title: trAttr('description'),
            field: 'description',
            type: 'string',
          },
          {
            field: 'pack_type',
            type: 'string',
            title: trAttr('pack_type'),
            // eslint-disable-next-line react/display-name
            render: (rowData) => {
              return (
                <label
                  style={{
                    backgroundColor: packagesColors[rowData.pack_type],
                    padding: 4,
                    color: 'white',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >{`${packageOptions[rowData.pack_type]}`}</label>
              );
            },
          },
          { title: trAttr('quantity'), field: 'quantity', type: 'string' },
          { field: 'unlimited', type: 'boolean', title: trAttr('unlimited') },
          { title: trAttr('bin'), field: 'bin', type: 'string' },
          {
            title: trAttr('created_at'),
            field: 'created_at',
            type: 'datetime',
          },
          {
            title: trAttr('updated_at'),
            field: 'updated_at',
            type: 'datetime',
          },
        ]}
        context={ProductContext}
        recordMap={(record) => ({
          ...record,
          created_at: record.created_at
            ? moment(record.created_at).format('DD/MM/YYYY HH:mm')
            : null,
          updated_at: record.updated_at
            ? moment(record.updated_at).format('DD/MM/YYYY HH:mm')
            : null,
        })}
      />

      <ModalDelete
        id={modalInfo.id}
        name={modalInfo.title}
        modal={modalInfo.show}
        deleteAction={handleDelete}
        onClose={() => setModalInfo({ show: false })}
      />
    </div>
  );
}

export default React.memo(ProductList);
